import { forwardRef, type ReactNode } from 'react';
import { ScrollArea, ScrollBar } from '@/app/_components/core/scroll-area';
import {
  Tabs as ShadcnTabs,
  TabsList as ShadcnTabsList,
  TabsTrigger as ShadcnTabsTrigger,
  TabsContent as ShadcnTabsContent,
} from '@/app/_components/ui/tabs';
import { cn, emptyToUndefined } from '@/app/_lib/utils';

export interface TabItem {
  /**
   * Label displayed in the tab trigger
   */
  label: string;
  /**
   * Unique value identifier for the tab
   */
  value: string;
  /**
   * Content to display when tab is active
   */
  content: ReactNode;
  /**
   * Optional icon to display before label
   */
  icon?: ReactNode;
  /**
   * Whether this tab is disabled
   * @default false
   */
  disabled?: boolean;
}

export interface TabsProps {
  /**
   * Array of tab configurations
   */
  tabs?: TabItem[];
  /**
   * The controlled value of the active tab
   */
  value?: string;
  /**
   * Default value for uncontrolled tabs
   */
  defaultValue?: string;
  /**
   * Callback when active tab changes
   */
  onValueChange?: (value: string) => void;
  /**
   * Custom className for the root element
   */
  className?: string;
  /**
   * Custom className for the tabs list
   */
  listClassName?: string;
  /**
   * Full width - makes tabs list take full width of container
   * @default false
   */
  fullWidth?: boolean;
  /**
   * Enable horizontal scrolling for tabs that don't fit in one line
   * @default false
   */
  scrollable?: boolean;
  /**
   * Custom children for advanced usage
   */
  children?: ReactNode;
}

export const Tabs = forwardRef<HTMLDivElement, TabsProps>(
  (
    {
      tabs,
      value,
      defaultValue,
      onValueChange,
      className,
      listClassName,
      fullWidth = false,
      scrollable = false,
      children,
    },
    ref
  ) => {
    const renderTabs = () => {
      if (!tabs) return null;

      const tabsList = (
        <TabsList className={cn(fullWidth && 'w-full', listClassName)}>
          {tabs.map((tab) => (
            <TabsTrigger
              key={tab.value}
              value={tab.value}
              disabled={tab.disabled}
              className={cn(fullWidth && 'flex-1')}
            >
              {tab.icon}
              {tab.label}
            </TabsTrigger>
          ))}
        </TabsList>
      );

      return (
        <>
          {scrollable ? (
            <ScrollArea>
              {tabsList}
              <ScrollBar orientation="horizontal" />
            </ScrollArea>
          ) : (
            tabsList
          )}
          {tabs.map((tab) => (
            <TabsContent key={tab.value} value={tab.value}>
              {tab.content}
            </TabsContent>
          ))}
        </>
      );
    };

    return (
      <ShadcnTabs
        ref={ref}
        value={value}
        defaultValue={emptyToUndefined(defaultValue) ?? tabs?.[0]?.value}
        onValueChange={onValueChange}
        className={className}
      >
        {children || renderTabs()}
      </ShadcnTabs>
    );
  }
);

Tabs.displayName = 'Tabs';

/**
 * Re-export tabs subcomponents for advanced usage
 */
export const TabsList = ShadcnTabsList;
export const TabsTrigger = ShadcnTabsTrigger;
export const TabsContent = ShadcnTabsContent;

export default Tabs;
